import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft, 
  Pill, 
  FlaskConical, 
  Scan, 
  Scissors, 
  Plus, 
  Trash2, 
  Search,
  Database
} from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../lib/utils';
import { logger } from '../../lib/logger';

const MasterDataManager = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('medications');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Data states
  const [medications, setMedications] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [imagingTests, setImagingTests] = useState([]);
  const [procedures, setProcedures] = useState([]);
  
  // Modal states
  const [addMedicationModal, setAddMedicationModal] = useState(false);
  const [addLabTestModal, setAddLabTestModal] = useState(false);
  const [addImagingModal, setAddImagingModal] = useState(false);
  const [addProcedureModal, setAddProcedureModal] = useState(false);
  
  // Form states
  const [newMedication, setNewMedication] = useState({
    name: '',
    generic_name: '',
    category: '',
    dosage_forms: '',
    manufacturer: '',
    unit_price: ''
  });
  
  const [newLabTest, setNewLabTest] = useState({
    name: '',
    category: '',
    sample_type: '',
    normal_range: '',
    unit_price: '',
    turnaround_time: ''
  });
  
  const [newImaging, setNewImaging] = useState({
    name: '',
    category: '',
    body_part: '',
    preparation: '',
    unit_price: '',
    duration_minutes: ''
  });
  
  const [newProcedure, setNewProcedure] = useState({
    name: '',
    category: '',
    description: '',
    duration_minutes: '',
    unit_price: '',
    is_minor: true
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [medsRes, labsRes, imagingRes, procsRes] = await Promise.all([
        api.get('/platform/medications'),
        api.get('/platform/lab-tests'),
        api.get('/platform/imaging-tests'),
        api.get('/platform/procedures')
      ]);
      setMedications(medsRes.data.medications || []);
      setLabTests(labsRes.data.lab_tests || []);
      setImagingTests(imagingRes.data.imaging_tests || []);
      setProcedures(procsRes.data.procedures || []);
    } catch (error) {
      toast.error('Failed to load master data');
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Medication handlers
  const handleAddMedication = async () => {
    if (!newMedication.name) {
      toast.error('Medication name is required');
      return;
    }
    try {
      await api.post('/platform/medications', {
        ...newMedication,
        unit_price: parseFloat(newMedication.unit_price) || 0
      });
      toast.success(`Medication "${newMedication.name}" added successfully`);
      setAddMedicationModal(false);
      setNewMedication({ name: '', generic_name: '', category: '', dosage_forms: '', manufacturer: '', unit_price: '' });
      fetchAllData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to add medication'));
    }
  };

  const handleDeleteMedication = async (id, name) => {
    if (!window.confirm(`Delete medication "${name}"?`)) return;
    try {
      await api.delete(`/platform/medications/${id}`);
      toast.success('Medication deleted');
      fetchAllData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to delete medication'));
    }
  };

  // Lab Test handlers
  const handleAddLabTest = async () => {
    if (!newLabTest.name) {
      toast.error('Test name is required');
      return;
    }
    try {
      await api.post('/platform/lab-tests', {
        ...newLabTest,
        unit_price: parseFloat(newLabTest.unit_price) || 0
      });
      toast.success(`Lab test "${newLabTest.name}" added successfully`);
      setAddLabTestModal(false);
      setNewLabTest({ name: '', category: '', sample_type: '', normal_range: '', unit_price: '', turnaround_time: '' });
      fetchAllData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to add lab test'));
    }
  };

  const handleDeleteLabTest = async (id, name) => {
    if (!window.confirm(`Delete lab test "${name}"?`)) return;
    try {
      await api.delete(`/platform/lab-tests/${id}`);
      toast.success('Lab test deleted');
      fetchAllData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to delete lab test'));
    }
  };

  // Imaging handlers
  const handleAddImaging = async () => {
    if (!newImaging.name) {
      toast.error('Imaging test name is required');
      return;
    }
    try {
      await api.post('/platform/imaging-tests', {
        ...newImaging,
        unit_price: parseFloat(newImaging.unit_price) || 0,
        duration_minutes: parseInt(newImaging.duration_minutes) || 30
      });
      toast.success(`Imaging test "${newImaging.name}" added successfully`);
      setAddImagingModal(false);
      setNewImaging({ name: '', category: '', body_part: '', preparation: '', unit_price: '', duration_minutes: '' });
      fetchAllData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to add imaging test'));
    }
  };

  const handleDeleteImaging = async (id, name) => {
    if (!window.confirm(`Delete imaging test "${name}"?`)) return;
    try {
      await api.delete(`/platform/imaging-tests/${id}`);
      toast.success('Imaging test deleted');
      fetchAllData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to delete imaging test'));
    }
  };

  // Procedure handlers
  const handleAddProcedure = async () => {
    if (!newProcedure.name) {
      toast.error('Procedure name is required');
      return;
    }
    try {
      await api.post('/platform/procedures', {
        ...newProcedure,
        unit_price: parseFloat(newProcedure.unit_price) || 0,
        duration_minutes: parseInt(newProcedure.duration_minutes) || 30
      });
      toast.success(`Procedure "${newProcedure.name}" added successfully`);
      setAddProcedureModal(false);
      setNewProcedure({ name: '', category: '', description: '', duration_minutes: '', unit_price: '', is_minor: true });
      fetchAllData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to add procedure'));
    }
  };

  const handleDeleteProcedure = async (id, name) => {
    if (!window.confirm(`Delete procedure "${name}"?`)) return;
    try {
      await api.delete(`/platform/procedures/${id}`);
      toast.success('Procedure deleted');
      fetchAllData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to delete procedure'));
    }
  };

  // Filter data based on search
  const filterData = (data) => {
    if (!searchTerm) return data;
    return data.filter(item => 
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.generic_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  if (loading) {
    return <div className="p-8 text-center">Loading master data...</div>;
  }

  return (
    <div data-testid="master-data-page">
      <header className="page-header glass-header">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/platform')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope] flex items-center gap-2">
              <Database className="w-6 h-6 text-blue-600" />
              Master Data Management
            </h1>
            <p className="text-slate-500">Manage medications, lab tests, imaging, and procedures</p>
          </div>
        </div>
      </header>

      <div className="page-content max-w-7xl">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="medications" className="flex items-center gap-2">
              <Pill className="w-4 h-4" />
              Medications ({medications.length})
            </TabsTrigger>
            <TabsTrigger value="lab-tests" className="flex items-center gap-2">
              <FlaskConical className="w-4 h-4" />
              Lab Tests ({labTests.length})
            </TabsTrigger>
            <TabsTrigger value="imaging" className="flex items-center gap-2">
              <Scan className="w-4 h-4" />
              Imaging ({imagingTests.length})
            </TabsTrigger>
            <TabsTrigger value="procedures" className="flex items-center gap-2">
              <Scissors className="w-4 h-4" />
              Procedures ({procedures.length})
            </TabsTrigger>
          </TabsList>

          {/* Medications Tab */}
          <TabsContent value="medications">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Pill className="w-5 h-5 text-green-600" />
                    Medications / Medicines
                  </CardTitle>
                  <Button onClick={() => setAddMedicationModal(true)} className="bg-green-500 hover:bg-green-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Medication
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filterData(medications).length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No medications found</p>
                  ) : (
                    filterData(medications).map((med) => (
                      <div key={med.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{med.name}</p>
                            {med.is_system && <Badge variant="outline" className="text-xs">System</Badge>}
                          </div>
                          <p className="text-sm text-slate-500">
                            {med.generic_name && `${med.generic_name} • `}
                            {med.category && `${med.category} • `}
                            {med.unit_price > 0 && `₹${med.unit_price}`}
                          </p>
                        </div>
                        {!med.is_system && (
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteMedication(med.id, med.name)} className="text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Lab Tests Tab */}
          <TabsContent value="lab-tests">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FlaskConical className="w-5 h-5 text-blue-600" />
                    Laboratory Tests
                  </CardTitle>
                  <Button onClick={() => setAddLabTestModal(true)} className="bg-blue-500 hover:bg-blue-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Lab Test
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filterData(labTests).length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No lab tests found</p>
                  ) : (
                    filterData(labTests).map((test) => (
                      <div key={test.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{test.name}</p>
                            {test.is_system && <Badge variant="outline" className="text-xs">System</Badge>}
                          </div>
                          <p className="text-sm text-slate-500">
                            {test.category && `${test.category} • `}
                            {test.sample_type && `${test.sample_type} • `}
                            {test.unit_price > 0 && `₹${test.unit_price}`}
                          </p>
                        </div>
                        {!test.is_system && (
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteLabTest(test.id, test.name)} className="text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Imaging Tab */}
          <TabsContent value="imaging">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Scan className="w-5 h-5 text-purple-600" />
                    Imaging Tests
                  </CardTitle>
                  <Button onClick={() => setAddImagingModal(true)} className="bg-purple-500 hover:bg-purple-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Imaging Test
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filterData(imagingTests).length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No imaging tests found</p>
                  ) : (
                    filterData(imagingTests).map((test) => (
                      <div key={test.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{test.name}</p>
                            {test.is_system && <Badge variant="outline" className="text-xs">System</Badge>}
                          </div>
                          <p className="text-sm text-slate-500">
                            {test.category && `${test.category} • `}
                            {test.body_part && `${test.body_part} • `}
                            {test.unit_price > 0 && `₹${test.unit_price}`}
                          </p>
                        </div>
                        {!test.is_system && (
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteImaging(test.id, test.name)} className="text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Procedures Tab */}
          <TabsContent value="procedures">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Scissors className="w-5 h-5 text-orange-600" />
                    Medical Procedures
                  </CardTitle>
                  <Button onClick={() => setAddProcedureModal(true)} className="bg-orange-500 hover:bg-orange-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Procedure
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {filterData(procedures).length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No procedures found</p>
                  ) : (
                    filterData(procedures).map((proc) => (
                      <div key={proc.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{proc.name}</p>
                            {proc.is_system && <Badge variant="outline" className="text-xs">System</Badge>}
                            {proc.is_minor ? (
                              <Badge className="bg-green-100 text-green-700">Minor</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700">Major</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">
                            {proc.category && `${proc.category} • `}
                            {proc.duration_minutes && `${proc.duration_minutes} mins • `}
                            {proc.unit_price > 0 && `₹${proc.unit_price}`}
                          </p>
                        </div>
                        {!proc.is_system && (
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteProcedure(proc.id, proc.name)} className="text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Medication Modal */}
      <Dialog open={addMedicationModal} onOpenChange={setAddMedicationModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Medication</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Medication Name *</Label>
              <Input
                value={newMedication.name}
                onChange={(e) => setNewMedication({...newMedication, name: e.target.value})}
                placeholder="e.g., Paracetamol 500mg"
              />
            </div>
            <div>
              <Label>Generic Name</Label>
              <Input
                value={newMedication.generic_name}
                onChange={(e) => setNewMedication({...newMedication, generic_name: e.target.value})}
                placeholder="e.g., Acetaminophen"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={newMedication.category} onValueChange={(v) => setNewMedication({...newMedication, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="analgesic">Analgesic</SelectItem>
                    <SelectItem value="antibiotic">Antibiotic</SelectItem>
                    <SelectItem value="antiviral">Antiviral</SelectItem>
                    <SelectItem value="cardiovascular">Cardiovascular</SelectItem>
                    <SelectItem value="respiratory">Respiratory</SelectItem>
                    <SelectItem value="gastrointestinal">Gastrointestinal</SelectItem>
                    <SelectItem value="neurological">Neurological</SelectItem>
                    <SelectItem value="dermatological">Dermatological</SelectItem>
                    <SelectItem value="supplement">Supplement</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit Price (₹)</Label>
                <Input
                  type="number"
                  value={newMedication.unit_price}
                  onChange={(e) => setNewMedication({...newMedication, unit_price: e.target.value})}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <Label>Dosage Forms</Label>
              <Input
                value={newMedication.dosage_forms}
                onChange={(e) => setNewMedication({...newMedication, dosage_forms: e.target.value})}
                placeholder="e.g., Tablet, Syrup, Injection"
              />
            </div>
            <div>
              <Label>Manufacturer</Label>
              <Input
                value={newMedication.manufacturer}
                onChange={(e) => setNewMedication({...newMedication, manufacturer: e.target.value})}
                placeholder="e.g., Cipla"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMedicationModal(false)}>Cancel</Button>
            <Button onClick={handleAddMedication} className="bg-green-500 hover:bg-green-600">Add Medication</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Lab Test Modal */}
      <Dialog open={addLabTestModal} onOpenChange={setAddLabTestModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Lab Test</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Test Name *</Label>
              <Input
                value={newLabTest.name}
                onChange={(e) => setNewLabTest({...newLabTest, name: e.target.value})}
                placeholder="e.g., Complete Blood Count (CBC)"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={newLabTest.category} onValueChange={(v) => setNewLabTest({...newLabTest, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hematology">Hematology</SelectItem>
                    <SelectItem value="biochemistry">Biochemistry</SelectItem>
                    <SelectItem value="microbiology">Microbiology</SelectItem>
                    <SelectItem value="immunology">Immunology</SelectItem>
                    <SelectItem value="pathology">Pathology</SelectItem>
                    <SelectItem value="molecular">Molecular Diagnostics</SelectItem>
                    <SelectItem value="urinalysis">Urinalysis</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sample Type</Label>
                <Select value={newLabTest.sample_type} onValueChange={(v) => setNewLabTest({...newLabTest, sample_type: v})}>
                  <SelectTrigger><SelectValue placeholder="Select sample" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blood">Blood</SelectItem>
                    <SelectItem value="urine">Urine</SelectItem>
                    <SelectItem value="stool">Stool</SelectItem>
                    <SelectItem value="swab">Swab</SelectItem>
                    <SelectItem value="tissue">Tissue</SelectItem>
                    <SelectItem value="csf">CSF</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unit Price (₹)</Label>
                <Input
                  type="number"
                  value={newLabTest.unit_price}
                  onChange={(e) => setNewLabTest({...newLabTest, unit_price: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Turnaround Time</Label>
                <Input
                  value={newLabTest.turnaround_time}
                  onChange={(e) => setNewLabTest({...newLabTest, turnaround_time: e.target.value})}
                  placeholder="e.g., 24 hours"
                />
              </div>
            </div>
            <div>
              <Label>Normal Range</Label>
              <Input
                value={newLabTest.normal_range}
                onChange={(e) => setNewLabTest({...newLabTest, normal_range: e.target.value})}
                placeholder="e.g., 4.5-11.0 x10^9/L"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddLabTestModal(false)}>Cancel</Button>
            <Button onClick={handleAddLabTest} className="bg-blue-500 hover:bg-blue-600">Add Lab Test</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Imaging Modal */}
      <Dialog open={addImagingModal} onOpenChange={setAddImagingModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Imaging Test</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Test Name *</Label>
              <Input
                value={newImaging.name}
                onChange={(e) => setNewImaging({...newImaging, name: e.target.value})}
                placeholder="e.g., Chest X-Ray"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={newImaging.category} onValueChange={(v) => setNewImaging({...newImaging, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="x-ray">X-Ray</SelectItem>
                    <SelectItem value="ct-scan">CT Scan</SelectItem>
                    <SelectItem value="mri">MRI</SelectItem>
                    <SelectItem value="ultrasound">Ultrasound</SelectItem>
                    <SelectItem value="pet-scan">PET Scan</SelectItem>
                    <SelectItem value="mammography">Mammography</SelectItem>
                    <SelectItem value="fluoroscopy">Fluoroscopy</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Body Part</Label>
                <Input
                  value={newImaging.body_part}
                  onChange={(e) => setNewImaging({...newImaging, body_part: e.target.value})}
                  placeholder="e.g., Chest, Abdomen"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unit Price (₹)</Label>
                <Input
                  type="number"
                  value={newImaging.unit_price}
                  onChange={(e) => setNewImaging({...newImaging, unit_price: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={newImaging.duration_minutes}
                  onChange={(e) => setNewImaging({...newImaging, duration_minutes: e.target.value})}
                  placeholder="30"
                />
              </div>
            </div>
            <div>
              <Label>Preparation Instructions</Label>
              <Textarea
                value={newImaging.preparation}
                onChange={(e) => setNewImaging({...newImaging, preparation: e.target.value})}
                placeholder="e.g., No food 4 hours before test"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddImagingModal(false)}>Cancel</Button>
            <Button onClick={handleAddImaging} className="bg-purple-500 hover:bg-purple-600">Add Imaging Test</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Procedure Modal */}
      <Dialog open={addProcedureModal} onOpenChange={setAddProcedureModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Procedure</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Procedure Name *</Label>
              <Input
                value={newProcedure.name}
                onChange={(e) => setNewProcedure({...newProcedure, name: e.target.value})}
                placeholder="e.g., Wound Dressing"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={newProcedure.category} onValueChange={(v) => setNewProcedure({...newProcedure, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="surgical">Surgical</SelectItem>
                    <SelectItem value="diagnostic">Diagnostic</SelectItem>
                    <SelectItem value="therapeutic">Therapeutic</SelectItem>
                    <SelectItem value="cosmetic">Cosmetic</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                    <SelectItem value="dental">Dental</SelectItem>
                    <SelectItem value="ophthalmic">Ophthalmic</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={newProcedure.is_minor ? 'minor' : 'major'} onValueChange={(v) => setNewProcedure({...newProcedure, is_minor: v === 'minor'})}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minor">Minor Procedure</SelectItem>
                    <SelectItem value="major">Major Procedure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unit Price (₹)</Label>
                <Input
                  type="number"
                  value={newProcedure.unit_price}
                  onChange={(e) => setNewProcedure({...newProcedure, unit_price: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={newProcedure.duration_minutes}
                  onChange={(e) => setNewProcedure({...newProcedure, duration_minutes: e.target.value})}
                  placeholder="30"
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newProcedure.description}
                onChange={(e) => setNewProcedure({...newProcedure, description: e.target.value})}
                placeholder="Brief description of the procedure"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddProcedureModal(false)}>Cancel</Button>
            <Button onClick={handleAddProcedure} className="bg-orange-500 hover:bg-orange-600">Add Procedure</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterDataManager;
